export enum ANSWER_STREAM_SUBJECT_TYPE {
  TEAMS_ACTIVITY_SESSION = 'teams_activity_session',
  PROSUMER_ACTIVITY_SESSION = 'prosumer_activity_session',
  TEAMS_PROJECT_SESSION = 'teams_project_session',
  PROSUMER_PROJECT_SESSION = 'prosumer_project_session',
}

export const ANSWER_STREAM_SUBJECT_TYPES = Object.values(
  ANSWER_STREAM_SUBJECT_TYPE
);

export type AnswerStreamSubjectType = ANSWER_STREAM_SUBJECT_TYPE;

export type AnswerStreamEvent = {
  subjectType: AnswerStreamSubjectType;
  subjectId: number;
  questionSlug: string;
  streamId: string;
} &
  (
    | { type: 'answer-stream-start' }
    | {
        type: 'answer-stream-chunk';
        field: 'output' | 'review';
        /** Delta text to append, never the cumulative field value. */
        value: string;
      }
    | {
        type: 'answer-stream-end';
        isCorrect: boolean | null;
        output: string | null;
        review: string | null;
      }
    | { type: 'answer-stream-error'; message: string }
    | {
        type: 'answer-stream-state';
        status: 'pending' | 'streaming' | 'done' | 'error';
        isCorrect: boolean | null;
        output: string | null;
        review: string | null;
        message: string | null;
      }
  );

export function isAnswerStreamEvent(value: unknown): value is AnswerStreamEvent {
  if (!value || typeof value !== 'object') return false;
  const event = value as Record<string, unknown>;
  const hasEnvelope =
    ANSWER_STREAM_SUBJECT_TYPES.includes(
      event.subjectType as AnswerStreamSubjectType
    ) &&
    Number.isInteger(event.subjectId) &&
    typeof event.questionSlug === 'string' &&
    typeof event.streamId === 'string';

  if (!hasEnvelope) return false;

  switch (event.type) {
    case 'answer-stream-start':
      return true;
    case 'answer-stream-chunk':
      return (
        (event.field === 'output' || event.field === 'review') &&
        typeof event.value === 'string'
      );
    case 'answer-stream-end':
      return (
        isNullableBoolean(event.isCorrect) &&
        isNullableString(event.output) &&
        isNullableString(event.review)
      );
    case 'answer-stream-error':
      return typeof event.message === 'string';
    case 'answer-stream-state':
      return (
        ['pending', 'streaming', 'done', 'error'].includes(
          event.status as string
        ) &&
        isNullableBoolean(event.isCorrect) &&
        isNullableString(event.output) &&
        isNullableString(event.review) &&
        isNullableString(event.message)
      );
    default:
      return false;
  }
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNullableBoolean(value: unknown): value is boolean | null {
  return value === null || typeof value === 'boolean';
}
